import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { AnalyticsCard } from 'modules/Analytics/components/AnalyticsCard';
import { AgedBalanceBars } from 'modules/Analytics/components/charts';
import { OverdueTotal } from 'modules/Analytics/components/OverdueTotal';
import { useAgedBalancesSubscription } from 'shared/hooks/use-aged-balances-subscription';
import type { AgedBalanceCompact } from 'shared/hooks/use-load-aged-balance';
import { useRefreshBalance } from 'shared/hooks/use-refresh-balance';

const OVERDUE_BUCKETS = ['0', '30', '60', '90'] as const;

const INVOICES_BY_BUCKET = {
  '0': '/invoices?min_days_late=1&max_days_late=30&status=unpaid,recoverable&',
  '30': '/invoices?min_days_late=30&max_days_late=60&status=unpaid,recoverable&',
  '60': '/invoices?min_days_late=60&max_days_late=90&status=unpaid,recoverable&',
  '90': '/invoices?min_days_late=90&status=unpaid,recoverable&',
  notDue: '/invoices?max_days_late=0&status=unpaid,recoverable&',
};

type DashboardAgedBalanceProps = {
  agedBalanceCompact: Pick<AgedBalanceCompact, 'computedAt' | 'credits' | 'debits'>;
};

export const DashboardAgedBalance = ({ agedBalanceCompact }: DashboardAgedBalanceProps) => {
  const { t } = useTranslation();
  const { refreshBalance } = useRefreshBalance();
  useAgedBalancesSubscription();
  const { debits, credits } = agedBalanceCompact;
  const overdue = OVERDUE_BUCKETS.reduce(
    (sum, bucket) => sum + debits[bucket] - credits[bucket],
    0,
  );

  return (
    <AnalyticsCard
      title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}
      subtitle={
        <Group gap={6}>
          <Text size="sm" c="dimmed">
            {t(i18nKeys.ANALYTICS.GRAPH_UPDATED, {
              date: dayjs(agedBalanceCompact.computedAt).fromNow(),
            })}
          </Text>
          <Tooltip withArrow label={t(i18nKeys.ANALYTICS.REFRESH_BALANCE)} arrowSize={7}>
            <ActionIcon
              onClick={() => refreshBalance()}
              aria-label={t(i18nKeys.ANALYTICS.REFRESH_BALANCE)}
              variant="subtle"
              color="gray"
              size="sm"
            >
              <IconRefresh size={16} stroke={1.75} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
      actions={<OverdueTotal amount={overdue} />}
      panelProps={{ h: '100%' }}
    >
      <AgedBalanceBars agedBalanceCompact={agedBalanceCompact} links={INVOICES_BY_BUCKET} />
    </AnalyticsCard>
  );
};
